import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Trophy, Sparkles, X } from 'lucide-react';

/**
 * Spectacular winner celebration popup.
 * Triggered by AccountPage when user has an unread `winner_announcement`
 * notification with `celebrated=false`.
 */
export default function WinnerCelebrationModal({ open, onClose, notification }) {
  const [confettiPieces, setConfettiPieces] = useState([]);

  useEffect(() => {
    if (!open) return;
    // Generate 50 confetti pieces with random positions
    const pieces = Array.from({ length: 50 }, (_, i) => ({
      id: i,
      x: Math.random() * 100,
      delay: Math.random() * 0.6,
      duration: 2.5 + Math.random() * 2,
      rotate: Math.random() * 720 - 360,
      color: ['#D4AF37', '#E5C97A', '#FFD700', '#C9A961', '#F2E1A8'][Math.floor(Math.random() * 5)],
      size: 8 + Math.random() * 8,
    }));
    setConfettiPieces(pieces);
  }, [open]);

  if (!open || !notification) return null;

  const data = notification.data || {};

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.3 }}
          className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/85 backdrop-blur-md"
          onClick={onClose}
          data-testid="winner-celebration-modal"
        >
          {/* Confetti */}
          <div className="absolute inset-0 overflow-hidden pointer-events-none">
            {confettiPieces.map((p) => (
              <motion.div
                key={p.id}
                initial={{ y: -50, x: `${p.x}%`, rotate: 0, opacity: 1 }}
                animate={{ y: '110vh', rotate: p.rotate, opacity: [1, 1, 0] }}
                transition={{ duration: p.duration, delay: p.delay, ease: 'linear', repeat: Infinity, repeatDelay: 1 }}
                style={{ position: 'absolute', width: p.size, height: p.size, background: p.color, borderRadius: 2 }}
              />
            ))}
          </div>

          {/* Content */}
          <motion.div
            initial={{ scale: 0.5, y: 50 }}
            animate={{ scale: 1, y: 0 }}
            exit={{ scale: 0.8, opacity: 0 }}
            transition={{ type: 'spring', stiffness: 200, damping: 18 }}
            onClick={(e) => e.stopPropagation()}
            className="relative w-full max-w-lg rounded-3xl overflow-hidden shadow-[0_30px_80px_rgba(0,0,0,0.6)]"
            style={{ background: 'linear-gradient(180deg, #0E4936 0%, #08291F 100%)', border: '2px solid hsl(42 48% 56% / 0.5)' }}
          >
            {/* Gold corners */}
            <span className="absolute top-3 left-3 w-6 h-6 border-t-2 border-l-2 border-gold rounded-tl-md pointer-events-none" />
            <span className="absolute top-3 right-3 w-6 h-6 border-t-2 border-r-2 border-gold rounded-tr-md pointer-events-none" />
            <span className="absolute bottom-3 left-3 w-6 h-6 border-b-2 border-l-2 border-gold rounded-bl-md pointer-events-none" />
            <span className="absolute bottom-3 right-3 w-6 h-6 border-b-2 border-r-2 border-gold rounded-br-md pointer-events-none" />

            {/* Spotlight */}
            <div className="absolute inset-0 hero-spotlight pointer-events-none" />
            <div className="absolute inset-0 noise-overlay opacity-50 pointer-events-none" />

            <button
              onClick={onClose}
              className="absolute top-3 right-3 z-10 w-8 h-8 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center text-cream/70 hover:text-cream transition-colors"
              aria-label="Close"
            >
              <X className="w-4 h-4" />
            </button>

            <div className="relative px-7 sm:px-10 py-10 sm:py-12 flex flex-col items-center text-center">
              {/* Animated Trophy */}
              <motion.div
                animate={{
                  rotate: [-10, 10, -10, 10, 0],
                  scale: [1, 1.2, 1.1, 1.2, 1],
                }}
                transition={{ duration: 1.4, delay: 0.3 }}
                className="relative mb-4"
              >
                <div className="absolute inset-0 bg-gold/30 blur-2xl scale-150" />
                <div className="relative w-24 h-24 rounded-full bg-gold-gradient flex items-center justify-center shadow-[0_10px_40px_rgba(212,175,55,0.6)]">
                  <Trophy className="w-14 h-14 text-rolex-green" strokeWidth={2.5} />
                </div>
              </motion.div>

              <motion.p
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.6 }}
                className="gold-line mb-3"
              >
                <Sparkles className="w-3 h-3" /> Champion of WINLUXOR
              </motion.p>

              <motion.h2
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.7 }}
                className="font-display text-4xl sm:text-5xl font-extrabold tracking-tight text-cream mb-1"
              >
                Congratulations
              </motion.h2>

              <motion.p
                initial={{ opacity: 0, scale: 0.92 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: 0.85, type: 'spring' }}
                className="font-display text-2xl italic font-bold text-gold-gradient mb-5"
              >
                You won!
              </motion.p>

              {data.image && (
                <motion.div
                  initial={{ opacity: 0, scale: 0.85 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: 1, type: 'spring' }}
                  className="relative mb-5"
                >
                  <div className="absolute -inset-3 bg-gold/30 blur-2xl rounded-3xl" />
                  <img src={data.image} alt={data.raffle_title} className="relative w-full max-w-xs h-44 object-cover rounded-2xl border-2 border-[hsl(var(--gold)/0.5)] shadow-[0_15px_40px_rgba(0,0,0,0.4)]" />
                </motion.div>
              )}

              <motion.h3
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 1.1 }}
                className="font-display text-2xl font-bold text-cream mb-1"
              >
                {data.raffle_title || notification.title}
              </motion.h3>

              {data.product_value && (
                <motion.p
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 1.2 }}
                  className="font-mono text-xl font-bold text-gold-gradient mb-6"
                >
                  Worth £{Number(data.product_value).toLocaleString()}
                </motion.p>
              )}

              <motion.div
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 1.3 }}
                className="rounded-xl bg-[hsl(var(--gold)/0.10)] border border-[hsl(var(--gold)/0.30)] p-4 mb-6 max-w-md"
              >
                <p className="text-sm text-cream/85 leading-relaxed">
                  Our team will personally contact you within <strong className="text-gold">48 hours</strong> to coordinate the handover of your prize.
                </p>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 1.45 }}
                className="w-full"
              >
                <button
                  onClick={onClose}
                  className="w-full btn-gold btn-press rounded-xl py-4 font-bold text-sm tracking-[0.18em] uppercase"
                  data-testid="celebrate-close-btn"
                >
                  🥂 I'm thrilled!
                </button>
              </motion.div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
